import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Autocomplete,
  Checkbox,
  TextField,
  Box,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { Users } from 'lucide-mui';
import {
  TEAM_SPACE_USER_ROLES,
  TeamSpaceUserRoleType,
} from '../../../constants/companySharedConstants';
import {
  setMembersAndNewUserEmails,
  setRole,
  sendInvitations,
  reset,
} from './inviteSlice';
import ActionDialog from '../../globalModals/ActionDialog';
import { getValidEmail } from '../../../lib/textUtils';
import type { GlobalDialogProps, Member } from '../../../types';
import { RootState } from '../../../reducers';

export const InviteModal = ({ onClose }: GlobalDialogProps) => {
  const { team, invite } = useSelector((state: RootState) => state);
  const teamMembers = team.selectedTeam?.teamSpaceMembers;
  const [invalidEmailError, setInvalidEmailError] = useState<string>('');
  const { invitation } = invite;
  const { members, folders: selectedFolders } = invitation;
  const theme = useTheme();
  const dispatch = useDispatch();
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    dispatch(reset());
  };

  const handleMemberChange = (
    event: React.SyntheticEvent | null,
    options: (Member | string)[]
  ) => {
    const memberObjects = options.filter(
      (m) => typeof m !== 'string'
    ) as Member[];
    const newUserEmails = options.filter(
      (m) => typeof m === 'string'
    ) as string[];
    const invalidEmails = newUserEmails.filter(
      (email) => !getValidEmail(email)
    );
    if (invalidEmails.length) {
      setInvalidEmailError(
        `Please remove invalid emails: ${invalidEmails.join(', ')}`
      );
      return;
    }
    setInvalidEmailError('');
    dispatch(
      setMembersAndNewUserEmails({ members: memberObjects, newUserEmails })
    );
  };
  const handleRoleChange = (
    event: SelectChangeEvent<TeamSpaceUserRoleType>
  ) => {
    const role = event.target.value as TeamSpaceUserRoleType;
    if (Object.values(TEAM_SPACE_USER_ROLES).includes(role)) {
      dispatch(setRole(role));
    }
  };
  return (
    <ActionDialog
      title="Invite team members"
      open
      onClose={handleClose}
      actions={[
        {
          key: 'Invite',
          label: 'Invite',
          onClick: () => {
            dispatch(sendInvitations());
          },
          startIcon: <Users />,
          disabled:
            !members.length || !selectedFolders.length || !!invalidEmailError,
        },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
        }}
      >
        <Autocomplete
          multiple
          freeSolo
          options={teamMembers || []}
          disableCloseOnSelect
          onChange={handleMemberChange}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            return option.name;
          }}
          renderOption={(props, option) => {
            if (typeof option === 'string') {
              return (
                <li {...props}>
                  <Checkbox />
                  {option}
                </li>
              );
            }
            return (
              <li {...props}>
                <Checkbox checked={members.includes(option)} />
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Email or name"
              variant="outlined"
              error={!!invalidEmailError}
              helperText={invalidEmailError}
            />
          )}
        />
        <Select
          sx={{
            width: '100%',
          }}
          defaultValue={TEAM_SPACE_USER_ROLES.MEMBER as TeamSpaceUserRoleType}
          onChange={handleRoleChange}
          placeholder="Role"
          label="Role"
        >
          {Object.values(TEAM_SPACE_USER_ROLES).map((role) => (
            <MenuItem value={role}>{role}</MenuItem>
          ))}
        </Select>
      </Box>
    </ActionDialog>
  );
};

export default InviteModal;
